// src/components/AppLayout.js

import React, { useState, useEffect, useMemo } from 'react';
import { signOut } from 'firebase/auth';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../App';
import { appId } from '../config/firebaseConfig';
import { PERMISSIONS_CONFIG, ADMIN_EMAIL } from '../config/permissionsConfig';
import { ALL_PAGE_KEYS } from '../config/menuConfig';

import Sidebar from './Sidebar';
import MainContent from './MainContent';

// Recebemos as props 'currentPage' e 'navigateTo' do App.js
export default function AppLayout({ user, currentPage, navigateTo, navTrigger }) {
    // Removemos a definição do estado 'currentPage' daqui
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [userPermissions, setUserPermissions] = useState({});
    const [userUiItems, setUserUiItems] = useState({});
    const [loadingPermissions, setLoadingPermissions] = useState(true);

    const [allowedCompanies, setAllowedCompanies] = useState([]);

    useEffect(() => {
        if (!user) return;

        const permissionsCollectionPath = `/artifacts/${appId}/public/data/user_permissions`;
        const userDocRef = doc(db, permissionsCollectionPath, user.uid);

        let groupUnsubscribe = null;

        const unsubscribe = onSnapshot(userDocRef, (userSnap) => {
            if (userSnap.exists()) {
                const userData = userSnap.data();
                
                // Suporte a múltiplos grupos (Array) ou legado (String)
                const groupIds = userData.groupIds || (userData.groupId ? [userData.groupId] : []);

                if (groupIds.length > 0) {
                    if (groupUnsubscribe) groupUnsubscribe();
                    
                    // Escutamos a coleção de grupos completa para facilitar o merge
                    groupUnsubscribe = onSnapshot(collection(db, `/artifacts/${appId}/public/data/groups`), (groupsSnap) => {
                        const allGroups = groupsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
                        const userGroups = allGroups.filter(g => groupIds.includes(g.id));

                        // --- MERGE CUMULATIVO (PADRÃO MICROSOFT) ---
                        let mergedPermissions = {};
                        let mergedUiItems = {};
                        let mergedCompanies = new Set();

                        userGroups.forEach(group => {
                            // 1. Merge de Permissões (O maior nível vence: edit > read > none)
                            if (group.permissions) {
                                Object.keys(group.permissions).forEach(pageId => {
                                    const currentLvl = mergedPermissions[pageId] || 'none';
                                    const groupLvl = group.permissions[pageId];
                                    
                                    if (groupLvl === 'edit' || (groupLvl === 'read' && currentLvl === 'none')) {
                                        mergedPermissions[pageId] = groupLvl;
                                    }
                                });
                            }

                        });
                        
                        // --- APLICA OVERRIDES INDIVIDUAIS (CIRÚRGICO) ---
                        // Regra: O Grupo é o "Gateway". Se o grupo não libera a página, o override não tem efeito.
                        const finalPermissions = {};
                        Object.keys(mergedPermissions).forEach(pageId => {
                            if (mergedPermissions[pageId] && mergedPermissions[pageId] !== 'none') {
                                // Se houver um override (edit/read), ele vence. Caso contrário, usa o do grupo.
                                finalPermissions[pageId] = userData.permissions?.[pageId] || mergedPermissions[pageId];
                            }
                        });
                        
                        // Admin sempre tem acesso total a todas as páginas, independente dos grupos
                        if (user.email === ADMIN_EMAIL) {
                            ALL_PAGE_KEYS.forEach(pageId => { finalPermissions[pageId] = 'edit'; });
                        }

                        // Itens de UI agora são estritamente individuais (não vêm mais dos grupos)
                        const finalUiItems = userData.uiItems || {};

                        setUserPermissions(finalPermissions);
                        setUserUiItems(finalUiItems);

                        if (user.email === ADMIN_EMAIL || finalPermissions['manage-access'] === 'edit') {
                            setAllowedCompanies(null);
                            setLoadingPermissions(false);
                        } else {
                            // Se o usuário tem empresas específicas no seu perfil (Override), elas vencem tudo
                            const userOverrideCompanies = userData.allowedCompanies;
                            if (userOverrideCompanies && userOverrideCompanies.length > 0) {
                                setAllowedCompanies(userOverrideCompanies);
                                setLoadingPermissions(false);
                            } else {
                                // BUSCA DINÂMICA: Quais empresas o usuário está na lista 'allowedUsers'?
                                // Como temos duas coleções de clientes, precisamos escutar ambas
                                const clientsPath = `/artifacts/${appId}/public/data/clients`;
                                const avulsosPath = `/artifacts/${appId}/public/data/clients-avulsos`;
                                
                                // Nota: Para performance em produção com milhares de clientes, 
                                // o ideal seria um campo no User, mas seguindo o desejo de "gestão na empresa":
                                onSnapshot(collection(db, clientsPath), (snap1) => {
                                    onSnapshot(collection(db, avulsosPath), (snap2) => {
                                        const allowedNames = [];
                                        [...snap1.docs, ...snap2.docs].forEach(doc => {
                                            const data = doc.data();
                                            if (data.allowedUsers && data.allowedUsers.includes(user.uid)) {
                                                allowedNames.push(data.name);
                                            }
                                        });
                                        setAllowedCompanies(allowedNames);
                                        setLoadingPermissions(false);
                                    });
                                });
                            }
                        }
                    });
                } else {
                    // Sem grupo: usa apenas as permissões individuais
                    const indivPerms = userData.permissions || {};
                    setUserPermissions(indivPerms);
                    setUserUiItems(userData.uiItems || {});
                    
                    if (user.email === ADMIN_EMAIL || indivPerms['manage-access'] === 'edit') {
                        setAllowedCompanies(null);
                    } else {
                        setAllowedCompanies(userData.allowedCompanies || null);
                    }
                    setLoadingPermissions(false);
                }
            } else {
                // ... resto do código para admin/novo usuário
                const defaultPermissions = {};
                if (user.email === ADMIN_EMAIL) {
                    Object.keys(PERMISSIONS_CONFIG).forEach(pageKey => {
                        defaultPermissions[pageKey] = 'edit';
                    });
                    setAllowedCompanies(null);
                } else {
                    defaultPermissions['home'] = 'read';
                }
                setUserPermissions(defaultPermissions);
                setUserUiItems({});
                setLoadingPermissions(false);
            }
        }, (error) => {
            console.error("Erro ao buscar permissões:", error);
            setLoadingPermissions(false);
        });

        return () => {
            unsubscribe();
            if (groupUnsubscribe) groupUnsubscribe();
        };
    }, [user]);

    const [isSidebarPinned, setIsSidebarPinned] = useState(() => {
        return localStorage.getItem('arcus_sidebar_pinned') === 'true';
    });

    const handleTogglePin = () => {
        setIsSidebarPinned(prev => {
            const next = !prev;
            localStorage.setItem('arcus_sidebar_pinned', next);
            return next;
        });
    };

    const handleCloseSidebar = () => {
        setIsSidebarOpen(false);
    };

    const handleOpenSidebar = () => {
        setIsSidebarOpen(true);
    };

    return (
        <div className="flex h-screen bg-slate-900 text-white font-sans overflow-hidden">
            <Sidebar
                isOpen={isSidebarOpen}
                onClose={handleCloseSidebar}
                navigateTo={navigateTo}
                currentPage={currentPage}
                userEmail={user.email}
                userPermissions={userPermissions}
                loadingPermissions={loadingPermissions}
                isPinned={isSidebarPinned}
                onTogglePin={handleTogglePin}
            />
            <MainContent
                currentPage={currentPage}
                onOpenSidebar={handleOpenSidebar}
                userId={user.uid}
                userEmail={user.email}
                userPermissions={userPermissions}
                allowedCompanies={allowedCompanies}
                loadingPermissions={loadingPermissions}
                navigateTo={navigateTo}
                userUiItems={userUiItems}
                isSidebarPinned={isSidebarPinned}
                navTrigger={navTrigger}
            />
        </div>
    );
}